import { HeaderIcons } from '../shared/icons';
import React, { useState } from 'react';
import { GameStatistics } from '../../types/game';
import Header from '../shared/Header';
import { renderAvatar } from '../../utils/avatarUtils';
import { ALL_GAMES } from '../../config/games';
import { appVariant } from '../../config';
import { getGameDisplayName } from '../../i18n/helpers';
import { useTranslation } from '../../i18n';
import { StatisticsModal } from '../shared';
import MenuPageWrapper from '../menu/MenuPageWrapper';
import PremiumPage from '../menu/PremiumPage';
import './GameEndScreen.css';

interface GameEndScreenProps {
  finalLeaderboard: Array<{ playerId: string; playerName: string; score: number }>;
  gameStatistics: GameStatistics | null;
  onPlaySameGame: () => void;
  onPlayAnotherGame: () => void;
  onBackToGameSelection?: () => void;
  onGetPremium?: () => void;
  roomName?: string;
  gameType?: string;
  getPlayerAvatar?: (playerId: string) => string | undefined;
}

const getPackGameType = (): 'blitz' | 'team' | 'family' | 'party' => {
  const variantMap: Record<string, 'blitz' | 'team' | 'family' | 'party'> = {
      blitz: 'blitz',
      team: 'team',
      family: 'family',
      party: 'party',
      new: 'party',
      coop: 'blitz',
    };
  return variantMap[appVariant] || 'blitz';
};

const THEME = {
  primary: '#EC4899',
  primaryLight: 'rgba(236, 72, 153, 0.15)',
  primaryBorder: 'rgba(236, 72, 153, 0.3)',
  accent: '#F97316',
  accentLight: 'rgba(249, 115, 22, 0.15)',
  accentBorder: 'rgba(249, 115, 22, 0.3)',
};

const fullscreenStyle: React.CSSProperties = {
  minHeight: '100vh',
  width: '100%',
  background: 'linear-gradient(135deg, #0a0a0f 0%, #1a0a2e 25%, #16213e 50%, #0f3460 75%, #1a0a2e 100%)',
  backgroundSize: '400% 400%',
  animation: 'gradientShift 15s ease infinite',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  overflow: 'hidden',
};

const contentStyle: React.CSSProperties = {
  flex: 1,
  padding: 'clamp(1rem, 3vw, 2.5rem) clamp(1rem, 3vw, 2rem)',
  paddingTop: 'clamp(90px, 10vw, 6rem)',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  zIndex: 2,
};

export const GameEndScreen: React.FC<GameEndScreenProps> = ({
  finalLeaderboard,
  gameStatistics,
  onPlaySameGame,
  onPlayAnotherGame,
  onBackToGameSelection,
  onGetPremium,
  roomName,
  gameType,
  getPlayerAvatar,
}) => {
  const { t } = useTranslation();
  const [showStatistics, setShowStatistics] = useState(false);
  const [showPremiumPage, setShowPremiumPage] = useState(false);
  // Check if game type is pair matching
  const isPairMatching = gameType === 'pair-matching' || gameType === 'team-pair-matching' || gameType === 'party-pair-matching' || gameType === 'family-pair-matching';
  
  // Helper to check if answer is pair matching format
  const isPairMatchingAnswer = (answer: string): boolean => {
    if (typeof answer !== 'string') return false;
    try {
      const parsed = JSON.parse(answer);
      return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed);
    } catch {
      return false;
    }
  };
  
  // Format wrong answer based on game type
  const formatWrongAnswer = (answer: string): string => {
    if (isPairMatching && isPairMatchingAnswer(answer)) {
      try {
        const pairs = JSON.parse(answer) as Record<string, string>;
        const pairEntries = Object.entries(pairs);
        // Format as "Left → Right, Left → Right, ..."
        return pairEntries.map(([left, right]) => `${left} → ${right}`).join(', ');
      } catch {
        return answer;
      }
    }
    return answer;
  };

  const handleGetPremium = onGetPremium ?? (() => setShowPremiumPage(true));

  if (showPremiumPage) {
    const packGameType = getPackGameType();
    return (
      <MenuPageWrapper
        title={t('common.premium')}
        onMenuClick={() => {}}
        onBack={() => setShowPremiumPage(false)}
        gameType={packGameType}
      >
        <PremiumPage gameType={packGameType} />
      </MenuPageWrapper>
    );
  }
  
  // Get a random "crazy" wrong answer for display
  const getCraziestWrongAnswer = () => {
    if (!gameStatistics || gameStatistics.wrongAnswers.length === 0) return null;
    // Pick a random wrong answer (or could pick the longest/most interesting one)
    const randomIndex = Math.floor(Math.random() * gameStatistics.wrongAnswers.length);
    return gameStatistics.wrongAnswers[randomIndex];
  };

  const craziestWrongAnswer = getCraziestWrongAnswer();
  
  // Get game name from gameType
  const game = gameType ? ALL_GAMES.find(g => g.id === gameType) : null;
  const gameName = gameType ? getGameDisplayName(gameType) : t('common.game_one');
  const gameIcon = game?.icon || '🎮';
  
  // Check if this is Hot Takes
  const isHotTakes = gameType === 'hot-takes' || gameType === 'party-hot-takes' || gameType === 'team-hot-takes' || gameType === 'family-hot-takes';
  
  return (
    <div style={fullscreenStyle}>
      <Header
        leftButton={onBackToGameSelection ? { icon: HeaderIcons.back, onClick: onBackToGameSelection, tooltip: t('gameplay.backToGameSelection') } : undefined}
        rightButton={{
          icon: HeaderIcons.refresh,
          onClick: () => window.location.reload(),
          tooltip: t('common.tooltips.refreshPage')
        }}
        roomCode={roomName}
      />
      <div style={{ ...contentStyle, alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ maxWidth: '1440px', width: '100%' }}>
          <h1 style={{ fontSize: 'clamp(2rem, 5vw, 3rem)', textAlign: 'center', marginBottom: '2rem', color: '#fff' }}>
            {t('gameplay.gameEnd.completed', { icon: gameIcon, gameName })}
          </h1>
          
          {/* Two column layout: Leaderboard (2/3) and Statistics (1/3) */}
          <div className="game-end-screen__two-column" style={{ 
            display: 'grid', 
            gridTemplateColumns: '2fr 1fr', 
            gap: '2rem',
            alignItems: 'start',
            width: '100%'
          }}>
            {/* Left Column: Leaderboard - dynamic width, fills 2/3 */}
            <div style={{ minWidth: 0 }}>
              <div style={{
                background: 'rgba(15, 23, 42, 0.7)',
                backdropFilter: 'blur(20px)',
                borderRadius: '24px',
                padding: '2rem',
                border: '3px solid',
                borderImage: 'linear-gradient(135deg, rgba(236, 72, 153, 0.5), rgba(249, 115, 22, 0.5)) 1',
                boxShadow: '0 8px 32px rgba(236, 72, 153, 0.3)',
              }}>
                <h2 style={{ marginBottom: '1.5rem', color: THEME.primary, fontSize: 'clamp(1.2rem, 3vw, 1.5rem)', textAlign: 'center' }}>{t('gameplay.gameEnd.finalScores')}</h2>
                {finalLeaderboard.map((entry, index) => {
                  const medals = ['🥇', '🥈', '🥉'];
                  const avatar = getPlayerAvatar ? getPlayerAvatar(entry.playerId) : undefined;
                  return (
                    <div
                      key={entry.playerId}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem',
                        padding: '1rem',
                        borderBottom: index < finalLeaderboard.length - 1 ? '1px solid rgba(255,255,255,0.1)' : 'none',
                        fontSize: index < 3 ? 'clamp(1.1rem, 3vw, 1.3rem)' : 'clamp(1rem, 2.5vw, 1.1rem)',
                      }}
                    >
                      <span style={{ fontSize: 'clamp(1.5rem, 4vw, 2rem)', width: 'clamp(2rem, 5vw, 2.5rem)' }}>
                        {index < 3 ? medals[index] : `${index + 1}.`}
                      </span>
                      {renderAvatar(avatar, 'clamp(1.5rem, 4vw, 2rem)')}
                      <span style={{ flex: 1, fontWeight: index < 3 ? 'bold' : 'normal', color: '#fff' }}>{entry.playerName}</span>
                      <span style={{ fontWeight: 'bold', color: THEME.accent, fontSize: 'clamp(1.1rem, 3vw, 1.3rem)' }}>{entry.score} {t('common.pts')}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right Column: Statistics */}
            {gameStatistics && (
              <div className="game-end-screen__statistics" style={{ width: '100%' }}>
                <h2 className="game-end-screen__statistics-title">
                  {gameStatistics.hotTakesStats ? t('gameplay.hotTakes.summaryTitle') : t('gameplay.gameEnd.gameStatistics')}
                </h2>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {/* Hot Takes specific statistics */}
                  {gameStatistics.hotTakesStats && (
                    <>
                      {gameStatistics.hotTakesStats.mostDrinks && (
                        <div className="game-end-screen__stat-card game-end-screen__stat-card--error">
                          <div className="game-end-screen__stat-label">
                            {t('gameplay.hotTakes.mostDrinks')}
                          </div>
                          <div className="game-end-screen__stat-value">
                            {gameStatistics.hotTakesStats.mostDrinks.playerName}
                          </div>
                          <div className="game-end-screen__stat-subvalue">
                            {t('gameplay.hotTakes.drinksCount', { count: gameStatistics.hotTakesStats.mostDrinks.count })}
                          </div>
                        </div>
                      )}

                      {gameStatistics.hotTakesStats.totalTies > 0 && (
                        <div className="game-end-screen__stat-card game-end-screen__stat-card--warning">
                          <div className="game-end-screen__stat-label">
                            {t('gameplay.hotTakes.totalTies')}
                          </div>
                          <div className="game-end-screen__stat-value game-end-screen__stat-value--large">
                            {gameStatistics.hotTakesStats.totalTies}
                          </div>
                          <div className="game-end-screen__stat-subvalue">
                            {t('gameplay.hotTakes.everyoneDrank')}
                          </div>
                        </div>
                      )}

                      {gameStatistics.hotTakesStats.mostControversial && (
                        <div className="game-end-screen__stat-card game-end-screen__stat-card--primary">
                          <div className="game-end-screen__stat-label">
                            {t('gameplay.hotTakes.mostControversial')}
                          </div>
                          <div className="game-end-screen__stat-value game-end-screen__stat-value--quote" style={{ fontSize: '0.9rem', lineHeight: '1.4' }}>
                            "{gameStatistics.hotTakesStats.mostControversial.statement}"
                          </div>
                          <div className="game-end-screen__stat-subvalue">
                            {t('gameplay.hotTakes.votesSplit', { agree: gameStatistics.hotTakesStats.mostControversial.agreeCount, disagree: gameStatistics.hotTakesStats.mostControversial.disagreeCount })}
                          </div>
                        </div>
                      )}

                      {gameStatistics.totalQuestions !== undefined && gameStatistics.totalQuestions > 0 && (
                        <div className="game-end-screen__stat-card game-end-screen__stat-card--success">
                          <div className="game-end-screen__stat-label">
                            {t('gameplay.hotTakes.totalHotTakes')}
                          </div>
                          <div className="game-end-screen__stat-value game-end-screen__stat-value--large">
                            {gameStatistics.totalQuestions}
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {/* Regular game statistics (for non-Hot Takes games) */}
                  {!gameStatistics.hotTakesStats && (
                    <>
                      {gameStatistics.mostCorrectAnswers && gameStatistics.mostCorrectAnswers.count > 0 && (
                        <div className="game-end-screen__stat-card game-end-screen__stat-card--primary">
                          <div className="game-end-screen__stat-label">
                            {t('gameplay.gameEnd.mostCorrectAnswers')}
                          </div>
                          <div className="game-end-screen__stat-value">
                            {gameStatistics.mostCorrectAnswers.playerName}
                          </div>
                          <div className="game-end-screen__stat-subvalue">
                            {gameStatistics.mostCorrectAnswers.count} {t('common.correct')}
                          </div>
                        </div>
                      )}

                      {gameStatistics.averageScore !== undefined && (
                        <div className="game-end-screen__stat-card game-end-screen__stat-card--success">
                          <div className="game-end-screen__stat-label">
                            {t('gameplay.gameEnd.averageScore')}
                          </div>
                          <div className="game-end-screen__stat-value game-end-screen__stat-value--large">
                            {gameStatistics.averageScore}
                          </div>
                          <div className="game-end-screen__stat-subvalue">
                            {t('common.points')}
                          </div>
                        </div>
                      )}

                      {gameStatistics.totalQuestions !== undefined && gameStatistics.totalQuestions > 0 && (
                        <div className="game-end-screen__stat-card game-end-screen__stat-card--warning">
                          <div className="game-end-screen__stat-label">
                            {t('gameplay.gameEnd.totalQuestions')}
                          </div>
                          <div className="game-end-screen__stat-value game-end-screen__stat-value--large">
                            {gameStatistics.totalQuestions}
                          </div>
                        </div>
                      )}

                      {craziestWrongAnswer && (
                        <div className="game-end-screen__stat-card game-end-screen__stat-card--error">
                          <div className="game-end-screen__stat-label">
                            {t('gameplay.gameEnd.craziestWrongAnswer')}
                          </div>
                          <div className="game-end-screen__stat-value game-end-screen__stat-value--quote">
                            "{formatWrongAnswer(craziestWrongAnswer.answer)}"
                          </div>
                          <div className="game-end-screen__stat-subvalue game-end-screen__stat-subvalue--author">
                            {t('gameplay.gameEnd.byPlayer', { name: craziestWrongAnswer.playerName })}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Hot Takes Question Summary */}
            {isHotTakes && gameStatistics?.hotTakesResults && gameStatistics.hotTakesResults.length > 0 && (
              <div style={{ 
                gridColumn: '1 / -1', 
                marginTop: '2rem',
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '1.5rem',
                padding: '2rem',
                border: '2px solid rgba(255, 255, 255, 0.1)',
              }}>
                <h3 style={{ 
                  fontSize: '1.5rem', 
                  marginBottom: '1.5rem', 
                  textAlign: 'center',
                  color: '#F97316',
                }}>
                  {t('gameplay.hotTakes.allHotTakes')}
                </h3>
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '1rem',
                  maxHeight: '400px',
                  overflowY: 'auto',
                }}>
                  {gameStatistics.hotTakesResults.map((result, index) => (
                    <div key={result.questionId} style={{
                      background: 'rgba(0, 0, 0, 0.2)',
                      borderRadius: '1rem',
                      padding: '1rem',
                      border: `2px solid ${result.minorityVote === 'tie' ? 'rgba(249, 115, 22, 0.5)' : result.minorityVote === 'AGREE' ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
                    }}>
                      <div style={{ 
                        fontSize: '0.9rem', 
                        opacity: 0.7, 
                        marginBottom: '0.5rem',
                        color: '#F97316',
                      }}>
                        {t('gameplay.hotTakes.hotTakeNumber', { number: index + 1 })}
                      </div>
                      <div style={{ 
                        fontSize: '1.1rem', 
                        fontWeight: '600',
                        marginBottom: '0.75rem',
                        lineHeight: '1.4',
                      }}>
                        "{result.statement}"
                      </div>
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontSize: '0.9rem',
                      }}>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '0.5rem',
                          color: '#22C55E',
                        }}>
                          <span>👍</span>
                          <span>{t('gameplay.hotTakes.agreedCount', { count: result.agreeCount })}</span>
                        </div>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '0.5rem',
                          color: '#EF4444',
                        }}>
                          <span>👎</span>
                          <span>{t('gameplay.hotTakes.disagreedCount', { count: result.disagreeCount })}</span>
                        </div>
                        {result.minorityVote === 'tie' && (
                          <div style={{ 
                            fontSize: '0.85rem',
                            color: '#F97316',
                            fontWeight: 'bold',
                          }}>
                            {t('gameplay.hotTakes.tieLabel')}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          {/* Buttons Row */}
          <div style={{ 
            display: 'flex', 
            gap: '1rem', 
            justifyContent: 'center', 
            marginTop: '2rem',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={onPlaySameGame}
              style={{
                padding: 'clamp(1rem, 3vw, 1.25rem) clamp(2rem, 5vw, 3rem)',
                fontSize: 'clamp(1.1rem, 3vw, 1.3rem)',
                background: `linear-gradient(135deg, ${THEME.primary} 0%, ${THEME.accent} 100%)`,
                border: 'none',
                borderRadius: '1rem',
                color: 'white',
                cursor: 'pointer',
                fontWeight: 'bold',
                boxShadow: '0 8px 32px rgba(236, 72, 153, 0.5)',
              }}
            >
              {t('gameplay.gameEnd.playAgainEmoji')}
            </button>
            <button
              onClick={onBackToGameSelection || onPlayAnotherGame}
              style={{
                padding: 'clamp(1rem, 3vw, 1.25rem) clamp(2rem, 5vw, 3rem)',
                fontSize: 'clamp(1.1rem, 3vw, 1.3rem)',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '1rem',
                color: 'white',
                cursor: 'pointer',
                fontWeight: 'bold',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
              }}
            >
              {t('gameplay.gameEnd.playAnotherGameEmoji')}
            </button>
            {roomName && (
              <button
                data-testid="view-all-stats-button"
                onClick={() => setShowStatistics(true)}
                style={{
                  padding: 'clamp(1rem, 3vw, 1.25rem) clamp(2rem, 5vw, 3rem)',
                  fontSize: 'clamp(1.1rem, 3vw, 1.3rem)',
                  background: 'rgba(236, 72, 153, 0.2)',
                  border: '2px solid rgba(236, 72, 153, 0.4)',
                  borderRadius: '1rem',
                  color: 'white',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  boxShadow: '0 8px 32px rgba(236, 72, 153, 0.3)',
                }}
              >
                {t('gameplay.gameEnd.viewAllStats')}
              </button>
            )}
            <button
              data-testid="get-premium-button"
              onClick={handleGetPremium}
              style={{
                padding: 'clamp(1rem, 3vw, 1.25rem) clamp(2rem, 5vw, 3rem)',
                fontSize: 'clamp(1.1rem, 3vw, 1.3rem)',
                background: 'linear-gradient(135deg, #F59E0B 0%, #EC4899 100%)',
                border: 'none',
                borderRadius: '1rem',
                color: 'white',
                cursor: 'pointer',
                fontWeight: 'bold',
                boxShadow: '0 8px 32px rgba(245, 158, 11, 0.4)',
              }}
            >
              {t('gameplay.gameEnd.getPremium')}
            </button>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>
      {roomName && (
        <StatisticsModal
          isOpen={showStatistics}
          onClose={() => setShowStatistics(false)}
          roomName={roomName}
          getPlayerAvatar={getPlayerAvatar}
        />
      )}
    </div>
  );
};
